import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Flag,
  Sparkles,
  Target,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { cn } from '@/lib/utils';
import { STATUS_DATA_COLOUR } from '../theme/dataColours';
import { SetupCards } from '../components/setup/SetupCards';
import {
  calendarApi,
  calendarConflictsApi,
  dailyFocusBlocksApi,
  driversApi,
  planApi,
  preferencesApi,
  rhythmApi,
  workItemsApi,
} from '../api/services';
import type { WeeklyAlignment } from '../api/services';
import { driversById } from '../utils/drivers';
import { weeklyFocusTargetMinutes } from '../utils/focusCoverage';
import { isPlanStale, PLAN_REFRESH_EVERY_MS } from '../utils/todayPlan';
import type {
  AnnualStrategicDriver,
  CalendarConflict,
  CalendarEvent,
  CapacitySummary,
  DailyFocusBlock,
  DailyPlanView,
  LinkedWorkItemSummary,
  WorkItem,
} from '../types/models';
import { useAuth } from '../auth/AuthProvider';
import { useEntitlements } from '../context/EntitlementsContext';
import { colourForDriverId } from '../theme/tokens';
import { DayRibbon, type DayRibbonSegment } from '@/components/dashboard/DayRibbon';
import { StrategicPulseBand } from '@/components/dashboard/StrategicPulseBand';
import { CapacityStrip } from '@/components/dashboard/CapacityStrip';
import { deriveCapacity } from '../utils/dashboard';
import { openFocusSession } from '@/components/focus/FocusSessionFull';
import { providerCalendarId } from '../lib/calendarIds';
import { StillDoingList } from '@/components/dashboard/StillDoingList';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/components/ui/toast';
import { TryNewTodayOffer } from './today/TryNewToday';

// ---------------------------------------------------------------------------
// The workspace dashboard. Layout, top to bottom:
//   - Header: date, greeting, a reassurance line, "AI insights" and
//     "Open today's plan" (both go to /plan)
//   - Hero: today's DayRibbon (meetings, focus, personal) + the next action
//   - KPI strip: today's capacity, focus protected this week, strategic
//     alignment
//   - Today's priorities (or "Currently focusing on" inside a focus block)
//     beside Needs attention (today's calendar conflicts)
//   - Strategic Pulse band (per-driver gauges)
// Completing work happens on the Work Items page, not here.
//
// Strategic drivers are a leadership tool. On a plan without them the
// alignment KPI, the pulse band and the driver names on rows are left out
// (not shown empty), and the driver endpoints aren't called.
// ---------------------------------------------------------------------------

const TIMELINE_DAY_START_HOUR = 7;
const TIMELINE_DAY_END_HOUR = 19;

function formatDateOnly(d: Date): string {
  // Local-date components — same fix as FocusTimePage avoids the BST
  // off-by-one we shipped against last week.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function minutesFromDayStart(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h - TIMELINE_DAY_START_HOUR) * 60 + (m || 0);
}

function minutesFromDayStartDate(d: Date): number {
  return (d.getHours() - TIMELINE_DAY_START_HOUR) * 60 + d.getMinutes();
}

// Mirrors FocusSessionCard.tsx:83-86 — true when `now` falls inside the
// [startTime, endTime) window of the focus block on its `date`. Compared
// in local minute-of-day so we don't have to reconstruct a Date for the
// block boundaries on every tick.
function isCurrentlyInside(block: DailyFocusBlock, now: Date): boolean {
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (block.date !== todayKey) return false;
  const minuteOfDay = (hhmmss: string): number => {
    const [h = '0', m = '0'] = hhmmss.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };
  const cur = now.getHours() * 60 + now.getMinutes();
  return minuteOfDay(block.startTime) <= cur && minuteOfDay(block.endTime) > cur;
}


interface DayTuningEntry {
  id: string;
  kind: 'conflict-hard' | 'conflict-soft';
  title: string;
  detail: string;
  action?: { label: string; path: string };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const driversIncluded = useEntitlements().has('strategicDrivers');

  // Ticking `now` so the active focus-block detection updates as time
  // advances without requiring the user to refresh the page. 30 s is the
  // cheapest cadence that still flips the "Currently focusing on" header
  // within a minute of a block starting or ending. Lighter than the
  // FocusSessionCard's 1 Hz tick because the dashboard doesn't render a
  // live countdown — it only needs to know which block (if any) is live.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Today follows the clock: a tab left open overnight rolls over to the new
  // day (and reloads for it) instead of showing, and acting on, yesterday.
  const [today, setToday] = useState<Date>(() => new Date());
  useEffect(() => {
    if (formatDateOnly(now) !== formatDateOnly(today)) setToday(now);
  }, [now, today]);

  // Mount fade-in — page lifts opacity + translate-y on first render.
  // 200 ms ease-out matches the redesign's calm motion budget;
  // suppressed under `html.reduced-motion` via index.css.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setMounted(true), 16);
    return () => window.clearTimeout(id);
  }, []);
  // Week window — still loaded so the weekly focus-protected KPI has a
  // 7-day denominator. The 5-day timeline grid was removed in the
  // redesign; weekDates / setWeekStart are no longer needed.
  const weekStart = useMemo(() => getMonday(today), [today]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const [loading, setLoading] = useState(true);
  const [capacity, setCapacity] = useState<CapacitySummary | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  // This week's focus blocks, completed (Honoured) ones included so the
  // weekly total doesn't drop as focus gets done; the ribbon and "currently
  // focusing" framing use the still-scheduled ones.
  const [weekFocusBlocks, setWeekFocusBlocks] = useState<DailyFocusBlock[]>([]);
  const focusBlocks = useMemo(() => weekFocusBlocks.filter(b => b.status !== 'Honoured'), [weekFocusBlocks]);
  const [weekAlignment, setWeekAlignment] = useState<WeeklyAlignment | null>(null);
  const [focusTargetMinutes, setFocusTargetMinutes] = useState(0);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  // Today's saved plan: the same items, order and count as the morning email,
  // the Mac, iOS and Windows. The dashboard shows it; it doesn't choose its own.
  const [plan, setPlan] = useState<DailyPlanView | null>(null);
  // When the plan was last read: re-read quietly every 5 minutes while the tab
  // is visible, and offer Refresh once what's on screen is 30 minutes old.
  const [planFetchedAt, setPlanFetchedAt] = useState<number | null>(null);
  const planFetchedAtRef = useRef<number | null>(null);
  planFetchedAtRef.current = planFetchedAt;
  // The plan couldn't be read at all (not an empty day): say so, with Retry.
  const [planError, setPlanError] = useState(false);
  // Every plan read takes a number; only the newest may land, so an older
  // response arriving late (two events close together) can't replace a newer one.
  const planReadSeq = useRef(0);
  const takePlan = useCallback((seq: number, next: DailyPlanView) => {
    if (seq !== planReadSeq.current) return;
    setPlan(next);
    setPlanFetchedAt(Date.now());
    setPlanError(false);
  }, []);
  const readPlan = useCallback(async () => {
    const seq = ++planReadSeq.current;
    try {
      takePlan(seq, await planApi.getToday());
    } catch {
      if (seq === planReadSeq.current && planFetchedAtRef.current === null) setPlanError(true);
    }
  }, [takePlan]);
  const [conflicts, setConflicts] = useState<CalendarConflict[]>([]);

  const loadAllSeq = useRef(0);
  const loadAll = useCallback(async () => {
    const seq = ++loadAllSeq.current;
    const planSeq = ++planReadSeq.current;
    setLoading(true);
    try {
      const [capacityRes, evts, blocks, drvs, items, confs, alignment, prefs] =
        await Promise.allSettled([
          rhythmApi.getCapacity(formatDateOnly(today)),
          calendarApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
          dailyFocusBlocksApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd), undefined, true),
          driversIncluded ? driversApi.getActive() : Promise.resolve([] as AnnualStrategicDriver[]),
          planApi.getToday(),
          calendarConflictsApi.getByDate(formatDateOnly(today)),
          driversIncluded ? driversApi.getWeeklyAlignment() : Promise.resolve(null),
          preferencesApi.get(),
        ]);
      // A newer load started meanwhile: it will land instead.
      if (seq !== loadAllSeq.current) return;
      if (capacityRes.status === 'fulfilled') setCapacity(capacityRes.value);
      if (evts.status === 'fulfilled') setCalendarEvents(evts.value.filter(e => !e.isAllDay));
      if (blocks.status === 'fulfilled') setWeekFocusBlocks(blocks.value);
      if (drvs.status === 'fulfilled') setDrivers(drvs.value);
      if (items.status === 'fulfilled') takePlan(planSeq, items.value);
      else if (planSeq === planReadSeq.current && planFetchedAtRef.current === null) setPlanError(true);
      if (confs.status === 'fulfilled') setConflicts(confs.value);
      setWeekAlignment(alignment.status === 'fulfilled' ? alignment.value : null);
      if (prefs.status === 'fulfilled') setFocusTargetMinutes(weeklyFocusTargetMinutes(prefs.value));
    } finally {
      if (seq === loadAllSeq.current) setLoading(false);
    }
  }, [today, weekStart, weekEnd, driversIncluded, takePlan]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Cross-client sync — when another client (Mac app, EA on a different
  // browser) mutates a work item, AppLayout receives the SignalR broadcast
  // and dispatches cadence:workitem-changed. Refetch the Dashboard's local
  // state so the user sees the change without an F5.
  useEffect(() => {
    const onWorkItem = () => { void loadAll(); };
    // The saved plan changed (a 3-hourly check, an urgent change, an edit on
    // another device): re-read the plan alone, not the whole page.
    const onPlan = () => { void readPlan(); };
    window.addEventListener('cadence:workitem-changed', onWorkItem);
    window.addEventListener('cadence:plan-changed', onPlan);
    return () => {
      window.removeEventListener('cadence:workitem-changed', onWorkItem);
      window.removeEventListener('cadence:plan-changed', onPlan);
    };
  }, [loadAll, readPlan]);

  // A quiet re-read of the plan alone (no loading state, so nothing flickers):
  // every 5 minutes while the tab is visible, and when it becomes visible again.
  // Live events reload sooner; this covers a connection that quietly dropped.
  const lastQuietReadRef = useRef(0);
  useEffect(() => {
    // Offline or signed out, a read fails: what's on screen stays, and Refresh
    // appears at 30 minutes. A first read that failed is retried the same way.
    const quietRead = () => {
      const fetched = planFetchedAtRef.current;
      const t = Date.now();
      if (document.visibilityState !== 'visible') return;
      if (fetched !== null && t - fetched < PLAN_REFRESH_EVERY_MS) return;
      if (t - lastQuietReadRef.current < PLAN_REFRESH_EVERY_MS) return;
      lastQuietReadRef.current = t;
      void readPlan();
    };
    const id = window.setInterval(quietRead, 60_000);
    document.addEventListener('visibilitychange', quietRead);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', quietRead);
    };
  }, [readPlan]);
  const planIsStale = !loading && isPlanStale(planFetchedAt, now.getTime());

  const tuningEntries: DayTuningEntry[] = useMemo(
    () =>
      conflicts.map((c): DayTuningEntry => {
        const conflictKey = c.calendarEventId || c.workBlockId || `${c.startTime}|${c.description}`;
        return {
          id: `conflict:${c.severity}:${conflictKey}`,
          kind: c.severity === 'Hard' ? 'conflict-hard' : 'conflict-soft',
          title: c.severity === 'Hard' ? 'Calendar conflict' : 'Soft overlap',
          detail: c.description,
          action: { label: 'Resolve', path: '/calendar' },
        };
      }),
    [conflicts],
  );

  // Today's plan, in the plan's order. Work that didn't fit is "N more not
  // planned", and work rolling for weeks asks "Still doing these?" below.
  const activeItems = useMemo(() => plan?.items ?? [], [plan]);
  const stillDoing = useMemo(() => plan?.stillDoing ?? [], [plan]);
  const confirm = useConfirm();
  const { toast } = useToast();
  // Per item: two decisions in flight at once each keep their own buttons off.
  const [deciding, setDeciding] = useState<ReadonlySet<string>>(new Set());
  const decide = useCallback(async (item: WorkItem, action: 'keep' | 'drop') => {
    if (action === 'drop' && !(await confirm({
      title: `Drop "${item.title}"?`,
      body: 'It moves to Cancelled. You can find it in Work.',
      confirmLabel: 'Drop',
      destructive: true,
    }))) return;
    setDeciding(prev => new Set(prev).add(item.id));
    try {
      // The plan's own date (the person's today in their time zone), not the
      // browser's: a browser a day behind would add it to a day that's gone.
      if (action === 'keep') await workItemsApi.keep(item.id, plan?.date ?? formatDateOnly(today));
      else await workItemsApi.update(item.id, { status: 'Cancelled' });
      // "Still doing it" puts the item on today's plan (server-side); either
      // way the plan's lists change, so read it again.
      await readPlan();
    } catch {
      toast({ variant: 'error', message: action === 'keep' ? "Couldn't keep that item. Try again." : "Couldn't drop that item. Try again." });
    } finally {
      setDeciding(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, [confirm, toast, today, plan?.date, readPlan]);

  // Active focus block — the one the executive is *inside right now*.
  // When present, the "Today's priorities" card re-titles to
  // "Currently focusing on — {label}" and floats the block's
  // linkedWorkItems to the top of the list. Drives the calm "here's what
  // you're working on this hour" framing in the redesign.
  const activeBlock = useMemo(
    () => focusBlocks.find(b => isCurrentlyInside(b, now)) ?? null,
    [focusBlocks, now],
  );

  // Strip linked items out of activeItems so the same WorkItem doesn't
  // render twice (once at the top via linkedWorkItems, once below via the
  // regular day's list).
  const linkedItemIds = useMemo(() => {
    if (!activeBlock?.linkedWorkItems) return new Set<string>();
    return new Set(activeBlock.linkedWorkItems.map(w => w.id));
  }, [activeBlock]);
  // The block's items as the plan has them: only those still on it (a
  // finished item has left today's work), in the plan's order.
  const focusItems = useMemo(() => {
    const linked = new Map((activeBlock?.linkedWorkItems ?? []).map(l => [l.id, l] as const));
    return activeItems.filter(i => linked.has(i.id)).map(i => linked.get(i.id)!);
  }, [activeItems, activeBlock]);
  const laterItems = useMemo(
    () => activeItems.filter(i => !linkedItemIds.has(i.id)),
    [activeItems, linkedItemIds],
  );

  const greeting = useMemo(() => {
    const hour = today.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }, [today]);

  // Flatten today's events + focus blocks into DayRibbonSegment[].
  // Calendar events that look like focus blocks (title matches the
  // backend's focus pattern) tile as `focus`; events marked private
  // tile as `personal` (matches the FocusTimePage convention); the
  // rest are meetings.
  const todaySegments = useMemo<DayRibbonSegment[]>(() => {
    const todayKey = formatDateOnly(today);
    const segs: DayRibbonSegment[] = [];
    // Focus blocks may have been written back to the external calendar by
    // FocusTimeAllocationService.AllocateFocusTimeAsync — in which case the
    // same time window shows up as BOTH a focus block AND a calendar event.
    // Build a set of external-event-IDs the focus blocks claim so we can
    // skip those calendar events and avoid double-counting. (Bug 2026-06-01:
    // web read "8 focus blocks today" when there were really 4, because each
    // block was counted twice.) A written-back block holds the provider's raw
    // id while its synced twin carries a ms365_/google_ prefix, so compare
    // provider ids.
    const focusOwnedExternalIds = new Set<string>();
    for (const block of focusBlocks) {
      if (block.date !== todayKey) continue;
      if (block.externalCalendarEventId) focusOwnedExternalIds.add(providerCalendarId(block.externalCalendarEventId));
      if (block.linkedCalendarEventId) focusOwnedExternalIds.add(block.linkedCalendarEventId);
    }
    for (const evt of calendarEvents) {
      const dateStr = (evt.startTime ?? '').slice(0, 10);
      if (dateStr !== todayKey) continue;
      // Skip if this calendar event is the focus block's calendar twin.
      if (focusOwnedExternalIds.has(evt.id)
          || (evt.externalId && focusOwnedExternalIds.has(providerCalendarId(evt.externalId)))) continue;
      const start = minutesFromDayStartDate(new Date(evt.startTime));
      const end = minutesFromDayStartDate(new Date(evt.endTime));
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
      // Categorise using the canonical isPersonalEvent flag (set by
      // CalendarSource.IsPersonal — same field the backend uses everywhere
      // for the "exclude personal events from meeting counts" filter).
      // Previously fell back to a title-regex heuristic that mis-classified
      // any meeting with "personal" / "focus" / "block" in the title.
      const kind: DayRibbonSegment['kind'] =
        (evt as any).isPersonalEvent ? 'personal' : 'meeting';
      segs.push({
        kind,
        startMinutes: start + TIMELINE_DAY_START_HOUR * 60,
        endMinutes: end + TIMELINE_DAY_START_HOUR * 60,
        title: evt.title || (kind === 'meeting' ? 'Meeting' : 'Event'),
      });
    }
    for (const block of focusBlocks) {
      if (block.date !== todayKey) continue;
      const start = minutesFromDayStart(block.startTime);
      const end = minutesFromDayStart(block.endTime);
      if (end <= start) continue;
      segs.push({
        kind: 'focus',
        startMinutes: start + TIMELINE_DAY_START_HOUR * 60,
        endMinutes: end + TIMELINE_DAY_START_HOUR * 60,
        title: block.label || 'Focus time',
      });
    }
    return segs.sort((a, b) => a.startMinutes - b.startMinutes);
  }, [calendarEvents, focusBlocks, today]);

  // Reassurance subtitle — calm one-line summary of today's calendar
  // shape, mirroring the prototype's "Four meetings and three focus
  // blocks today, with the school run and lunch held around them."
  // Counts are dynamic. Falls back to a "plenty of room" line when
  // the calendar is empty so the page never reads as broken.
  const todayReassurance = useMemo(() => {
    const meets = todaySegments.filter(s => s.kind === 'meeting').length;
    const focusCount = todaySegments.filter(s => s.kind === 'focus').length;
    if (meets === 0 && focusCount === 0) {
      return 'Nothing on the calendar yet. Plenty of room to make today yours.';
    }
    const meetingsPart =
      meets === 0 ? 'No meetings' : `${meets} meeting${meets === 1 ? '' : 's'}`;
    const focusPart =
      focusCount === 0 ? 'no protected focus yet' : `${focusCount} focus block${focusCount === 1 ? '' : 's'}`;
    return `${meetingsPart} and ${focusPart} today.`;
  }, [todaySegments]);

  // The top of today's priorities (in-progress work first, then the most
  // important and longest waiting) drives the "Next action" callout under
  // the day ribbon. It used to take whichever item the API listed first,
  // which could be a low-priority one, or one rolling for weeks. Falls back
  // to the soonest scheduled focus block if no work item qualifies.
  const nextAction = useMemo(() => activeItems[0] ?? null, [activeItems]);

  // Focus protected this week (scheduled and completed) against the weekly
  // target from preferences — the same rule as the weekly focus card.
  const weekFocusProtected = useMemo(() => {
    const minutes = weekFocusBlocks.reduce((sum, b) => {
      const start = minutesFromDayStart(b.startTime);
      const end = minutesFromDayStart(b.endTime);
      return sum + Math.max(0, end - start);
    }, 0);
    return Math.round((minutes / 60) * 10) / 10; // hours, 1 dp
  }, [weekFocusBlocks]);

  const focusTargetHours = Math.round((focusTargetMinutes / 60) * 10) / 10;

  // Strategic alignment: the share of this week's meeting and work time on
  // driver-linked work (server-computed, time-weighted). Null until the week
  // has any time in it — shown as "—", never as 0%.
  const alignmentPct = weekAlignment?.alignmentPercent == null ? null : Math.round(weekAlignment.alignmentPercent);

  const driverMap = useMemo(
    () => driversById(driversIncluded ? drivers : []),
    [drivers, driversIncluded],
  );

  const heroDateLabel = today.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      {/* Calm-by-default backdrop. The previous version had four
          saturated blue/violet/amber/rose blobs at the page corners
          that bled through the SlimRail at the new 220px width and
          fought the muted card surfaces in front of them. Replaced
          with the page-level `background` token (slate in dark, off-
          white in light) so the surface reads quietly and the cards
          carry the colour through their driver tags + chips. */}

      <PageContainer
        className={cn(
          // The app shell's <main> pads the page; the kit's width.
          'flex flex-col gap-5 transition-[opacity,transform] duration-200 ease-out',
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1',
        )}
      >
        {/* Header: the date, the greeting and a reassurance line (a calm
            "here's what to expect" sentence composed from what the
            calendar shape actually is today), with the two actions beside.
            mb-1: with the column's gap, the kit's 24px under the header. */}
        <PageHeader
          className="mb-1"
          eyebrow={heroDateLabel}
          title={<>{greeting}{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}.</>}
          subtitle={todayReassurance}
          actions={<>
            {/* AI insights — ghost-style button (card surface + hairline
                border + muted text) so it reads as the lower-emphasis
                action next to the primary "Open today's plan". Clicking
                opens the planning surface where coaching, conflict
                detection and time-allocation hints surface contextually. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/plan')}
                  className="gap-[7px] border-border bg-card px-[15px] py-[9px] text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Sparkles className="h-[15px] w-[15px]" />
                  AI insights
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Coaching, conflict detection and time-allocation hints surface contextually inside the plan and dashboard cards.
              </TooltipContent>
            </Tooltip>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/plan')}
              className="gap-[7px] px-[15px] py-[9px] text-sm font-semibold"
            >
              Open today's plan
              <ArrowRight className="h-[15px] w-[15px]" />
            </Button>
          </>}
        />

        {/* The new Today's trial (web app v4 Phase 4.7). */}
        <TryNewTodayOffer />

        {/* Guided setup, part two: the Claude checklist or the assistant card (or nothing). */}
        <SetupCards />

        {/* HERO — Day ribbon + one next action.
            The ribbon shows today shaped by event type (focus = glowing
            blue, meeting = neutral, personal = dashed lavender) and a
            live now-marker. Below it sits the single most important
            thing to do next — verb-first, calm, no clutter. */}
        <Card className="overflow-visible">
          <CardContent className="space-y-4 py-5">
            <div className="flex items-center justify-end gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-3.5 rounded-[2px] bg-gradient-to-b from-primary/40 to-primary"
                  style={{ boxShadow: '0 0 8px rgba(26,108,240,0.32)' }}
                />
                Protected focus
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-3.5 rounded-[2px] bg-foreground/40" />
                Meeting
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-3.5 rounded-[2px] border border-dashed border-chart-lavender bg-chart-lavender/[0.18]" />
                Personal
              </span>
            </div>
            <DayRibbon
              segments={todaySegments}
              startHour={TIMELINE_DAY_START_HOUR}
              endHour={TIMELINE_DAY_END_HOUR}
            />
            {nextAction && (
              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    Next action
                  </p>
                  <h2 className="mt-1.5 mb-2 truncate text-xl font-semibold tracking-[-0.01em]">
                    {nextAction.title}
                  </h2>
                  {/* Meta row — HH:MM mono start time (from the linked
                      scheduled block, falls back to derived) + driver
                      name with 8px dot + type + duration, per the
                      prototype's .hero-meta pattern. */}
                  <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    {nextAction.scheduledBlocks?.[0]?.startTime && (
                      <>
                        <span className="font-mono tabular-nums">
                          {nextAction.scheduledBlocks[0].startTime.slice(0, 5)}
                        </span>
                        <span aria-hidden className="text-muted-foreground/60">·</span>
                      </>
                    )}
                    {driversIncluded && (nextAction as any).strategicDriverId && (
                      <>
                        <span
                          aria-hidden
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ background: colourForDriverId((nextAction as any).strategicDriverId) }}
                        />
                        <span>
                          {drivers.find(d => d.id === (nextAction as any).strategicDriverId)?.title ?? 'Driver'}
                        </span>
                        <span aria-hidden className="text-muted-foreground/60">·</span>
                      </>
                    )}
                    <span>{nextAction.type}</span>
                    {nextAction.estimatedMinutes > 0 && (
                      <>
                        <span aria-hidden className="text-muted-foreground/60">·</span>
                        <span>{formatDuration(nextAction.estimatedMinutes)}</span>
                      </>
                    )}
                  </p>
                </div>
                <Button size="lg" onClick={() => openFocusSession()}>
                  Begin focus
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3-up KPI strip — capacity / focus protected / strategic alignment.
            Cards stretch to a uniform row height via the grid; each
            CardContent is a flex column so the progress bars at the
            bottom land at the same Y line across all three. Without the
            flex-col + mt-auto, the bars were drifting up/down because
            the "headline number" rows have different intrinsic heights
            (26pt mono vs CapacityStrip's small segmented row). */}
        <div
          className={cn(
            'grid gap-5',
            driversIncluded ? 'lg:grid-cols-[1.4fr_1fr_1fr]' : 'lg:grid-cols-[1.4fr_1fr]',
          )}
        >
          <Card>
            <CardContent className="flex h-full flex-col py-4">
              <p className="mb-3 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Today's capacity
              </p>
              {/* Segmented capacity strip — Committed / Protected / Free per
                  the prototype. CapacityStrip is the existing shared
                  component used by the EA surface; the semicircle gauge
                  treatment that briefly lived here drifted from spec. */}
              <CapacityStrip
                capacity={capacity ? deriveCapacity(capacity) : null}
                loading={loading}
              />
              {!loading && !capacity && (
                <p className="text-sm text-muted-foreground">Capacity isn't available for today yet.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex h-full flex-col py-4">
              <p className="mb-3 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Focus protected this week
              </p>
              <p className="font-mono text-2xl font-bold tracking-[-0.01em]">
                {weekFocusProtected}h{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  {focusTargetHours > 0 ? `of ${focusTargetHours}h target` : 'no weekly target set'}
                </span>
              </p>
              <Progress
                value={focusTargetHours > 0 ? Math.min(100, (weekFocusProtected / focusTargetHours) * 100) : 0}
                className="mt-auto h-1.5"
              />
            </CardContent>
          </Card>
          {driversIncluded && (
            <Card>
              <CardContent className="flex h-full flex-col py-4">
                <p className="mb-3 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Strategic alignment
                </p>
                <p className="font-mono text-2xl font-bold tracking-[-0.01em]">
                  {alignmentPct == null ? '—' : `${alignmentPct}%`}{' '}
                  <span className="font-sans text-sm font-normal text-muted-foreground">of this week's time</span>
                </p>
                {/* On-plan green for alignment — distinguishes the strategic
                    card from the focus-protected card (blue) above: the
                    status "success" data colour, deeper in light mode. */}
                <Progress value={alignmentPct ?? 0} className="mt-auto h-1.5" indicatorColor={STATUS_DATA_COLOUR.success} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Two-column: Today's priorities (left, broader) + Needs attention
            (right). Both surfaces reuse the existing ActiveWorkList and
            DayTuningFeed components — the data hooks are intact, only
            the layout is new. */}
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Target
                  className={cn(
                    'h-4 w-4',
                    activeBlock ? 'text-brand-amber' : 'text-primary',
                  )}
                />
                {activeBlock ? (
                  // Copper accent on the title when we're inside a focus
                  // block — matches the cross-surface "brand/hover" role
                  // (focus = blue, brand = copper) so the change in framing
                  // reads as intentional rather than a state error.
                  <span className="text-brand-amber">
                    Currently focusing on — {activeBlock.label || 'Deep work'}
                  </span>
                ) : (
                  "Today's priorities"
                )}
              </CardTitle>
              {planIsStale && planFetchedAt !== null && (
                <div role="status" className="mt-1 flex items-center gap-2 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-muted-foreground">
                  <span>Not updated since {format(new Date(planFetchedAt), 'HH:mm')}</span>
                  <button type="button" className="font-medium text-primary hover:underline" onClick={() => { void loadAll(); }}>
                    Refresh
                  </button>
                </div>
              )}
              {planError && plan === null && (
                <div role="alert" className="mt-1 flex items-center gap-2 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-muted-foreground">
                  <span>Couldn't load today's plan.</span>
                  <button type="button" className="font-medium text-primary hover:underline" onClick={() => { void readPlan(); }}>
                    Retry
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {planError && plan === null ? null : activeItems.length === 0
                  ? "Nothing planned for today. Open the plan to schedule what's next."
                  : `${plan?.toClear ?? activeItems.length} to clear today.`}
                {(plan?.notPlannedCount ?? 0) > 0 && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => navigate('/work')}
                    >
                      {plan!.notPlannedCount} more not planned
                    </button>
                  </>
                )}
              </p>
              {plan?.lastChangeReason && (
                <p className="text-xs text-muted-foreground" data-testid="plan-change-reason">
                  {plan.lastChangeReason}
                </p>
              )}
            </CardHeader>
            <CardContent className="pt-2">
              {activeBlock && focusItems.length > 0 ? (
                <>
                  <LinkedItemsList
                    items={focusItems}
                    showDrivers={driversIncluded}
                    blockStartTime={activeBlock.startTime}
                    onOpen={() => navigate('/work')}
                  />
                  {laterItems.length > 0 && (
                    <div className="mt-4 border-t border-border pt-3">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                        Later today
                      </p>
                      <ActiveWorkList
                        items={laterItems}
                        drivers={driverMap}
                        loading={loading}
                        onOpen={() => navigate('/work')}
                      />
                    </div>
                  )}
                </>
              ) : (
                <ActiveWorkList
                  items={activeItems}
                  drivers={driverMap}
                  loading={loading}
                  onOpen={() => navigate('/work')}
                />
              )}
            </CardContent>
          </Card>

          <div className="flex min-w-0 flex-col gap-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Needs attention
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Calendar conflicts on today's plan.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <DayTuningFeed
                entries={tuningEntries}
                loading={loading}
                onAction={(entry) => entry.action && navigate(entry.action.path)}
              />
            </CardContent>
          </Card>
          {stillDoing.length > 0 && (
            <Card>
              <CardContent className="pt-4">
                <StillDoingList
                  items={stillDoing}
                  busyIds={deciding}
                  onKeep={item => void decide(item, 'keep')}
                  onDrop={item => void decide(item, 'drop')}
                  onOpenAll={() => navigate('/work')}
                />
              </CardContent>
            </Card>
          )}
          </div>
        </div>

        {/* Full-width Strategic Pulse band — gauges instead of bars. */}
        {driversIncluded && <StrategicPulseBand drivers={drivers} alignmentPct={alignmentPct} />}
      </PageContainer>
    </>
  );
}

// ---------------------------------------------------------------------------
// Active Work list — prototype row schema: mono HH:MM start time on the left
// (42px column), title in the middle with the strategic-driver dot inline,
// driver name + type + duration on a meta line below, and a "High" chip on
// the right rendered only when priority === High. Medium / Low intentionally
// render no chip — the spec uses chip density to surface the things you
// actually need to triage today.
// ---------------------------------------------------------------------------

function formatDuration(minutes: number): string {
  if (!minutes || minutes < 1) return '';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// LinkedItemsList — slim row schema for the items planned inside the
// *active* focus block. Renders above ActiveWorkList when activeBlock is
// non-null. LinkedWorkItemSummary is a deliberately leaner shape than
// WorkItem (no scheduledBlocks, no nested strategicDriver) so we render a
// minimal row: title + driver dot/name + type + duration. Start time
// defaults to the block's startTime when shown.
// ---------------------------------------------------------------------------

function LinkedItemsList({
  items,
  showDrivers,
  blockStartTime,
  onOpen,
}: {
  items: LinkedWorkItemSummary[];
  /** False on a plan without strategic drivers. */
  showDrivers: boolean;
  blockStartTime: string;
  onOpen: () => void;
}) {
  // Trim seconds off the block start (HH:MM) for the leading time column.
  const shortStart = blockStartTime ? blockStartTime.slice(0, 5) : '—';
  return (
    <ul className="divide-y divide-border/40">
      {items.map(item => {
        const driverColor = colourForDriverId(item.strategicDriverId ?? undefined);
        const driverName = (showDrivers && item.strategicDriverTitle) || null;
        const duration = formatDuration(item.estimatedMinutes ?? 0);
        return (
          <li
            key={item.id}
            onClick={onOpen}
            className="grid cursor-pointer grid-cols-[42px_1fr_auto] items-baseline gap-3 px-1 py-2.5 text-sm transition-colors hover:bg-accent/30"
          >
            <span className="font-mono text-sm tabular-nums text-muted-foreground">
              {shortStart}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-base font-medium text-foreground">
                  {item.title}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                {driverName && (
                  <>
                    <span
                      aria-hidden
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: driverColor }}
                    />
                    <span className="truncate">{driverName}</span>
                    <span aria-hidden className="text-muted-foreground/60">·</span>
                  </>
                )}
                <span>{item.type}</span>
                {duration && (
                  <>
                    <span aria-hidden className="text-muted-foreground/60">·</span>
                    <span>{duration}</span>
                  </>
                )}
              </div>
            </div>
            {item.priority === 'High' ? (
              <Badge variant="danger" className="self-center justify-self-end">High</Badge>
            ) : (
              <span aria-hidden />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ActiveWorkList({
  items,
  drivers,
  loading,
  onOpen,
}: {
  items: WorkItem[];
  drivers: Map<string, AnnualStrategicDriver>;
  loading: boolean;
  onOpen: () => void;
}) {
  if (!loading && items.length === 0) {
    return (
      <div className="py-6 text-center">
        <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-500" />
        <p className="mt-2 text-sm font-medium">Nothing active for today.</p>
        <p className="text-xs text-muted-foreground">
          Plan your day to populate — or add a work item.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ul className="divide-y divide-border/40">
        {items.map(item => {
          const driverColor = colourForDriverId(item.strategicDriverId);
          const driverName = (item.strategicDriverId && drivers.get(item.strategicDriverId)?.title) || null;
          // The API sends HH:mm:ss; the column fits HH:mm.
          const startTime = item.scheduledBlocks?.[0]?.startTime?.slice(0, 5) ?? null;
          const duration = formatDuration(item.estimatedMinutes);
          return (
            <li
              key={item.id}
              onClick={onOpen}
              className="grid cursor-pointer grid-cols-[42px_1fr_auto] items-baseline gap-3 px-1 py-2.5 text-sm transition-colors hover:bg-accent/30"
            >
              {/* HH:MM mono start time. 42px fixed column so titles align
                  regardless of whether a row has a time yet. */}
              <span className="font-mono text-sm tabular-nums text-muted-foreground">
                {startTime ?? '—'}
              </span>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-base font-medium text-foreground">
                    {item.title}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  {driverName && (
                    <>
                      <span
                        aria-hidden
                        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: driverColor }}
                      />
                      <span className="truncate">{driverName}</span>
                      <span aria-hidden className="text-muted-foreground/60">·</span>
                    </>
                  )}
                  <span>{item.type}</span>
                  {duration && (
                    <>
                      <span aria-hidden className="text-muted-foreground/60">·</span>
                      <span>{duration}</span>
                    </>
                  )}
                </div>
              </div>
              {item.priority === 'High' ? (
                <Badge variant="danger" className="self-center justify-self-end">High</Badge>
              ) : (
                <span aria-hidden />
              )}
            </li>
          );
        })}
      </ul>
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpen}
        className="mt-2 self-start px-2"
      >
        View all work items
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Needs Attention feed — today's calendar conflicts (hard breaches and
// soft overlaps). Each row opens the calendar to resolve it.
// ---------------------------------------------------------------------------

function DayTuningFeed({
  entries,
  loading,
  onAction,
}: {
  entries: DayTuningEntry[];
  loading: boolean;
  onAction: (entry: DayTuningEntry) => void;
}) {
  if (!loading && entries.length === 0) {
    return (
      <div className="py-4 text-center">
        <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-500" />
        <p className="mt-1.5 text-sm font-medium">Nothing needs attention.</p>
        <p className="text-xs text-muted-foreground">
          No conflicts or breaches on today's plan.
        </p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {entries.map(entry => {
        const isBreach = entry.kind === 'conflict-hard';
        const palette = isBreach
          ? 'bg-rose-50/40 dark:bg-rose-500/10'
          : 'bg-amber-50/50 dark:bg-amber-500/10';
        const Icon = isBreach ? AlertTriangle : Flag;
        const iconColor = isBreach ? 'text-rose-500' : 'text-amber-500';
        return (
          <li key={entry.id}>
            <button
              type="button"
              disabled={!entry.action}
              onClick={() => onAction(entry)}
              className={cn(
                'flex w-full items-start gap-2.5 rounded-[10px] p-3 text-left text-xs transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                palette,
                entry.action && 'cursor-pointer hover:brightness-105',
              )}
            >
              <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconColor)} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold leading-tight">{entry.title}</span>
                <span className="mt-0.5 block text-xs leading-[1.45] text-muted-foreground">{entry.detail}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
